import {useQueryClient} from '@github-ui/react-query'
import {useOverviewRouteQueryKey} from '../data/use-overview-route-data'
import {useLayoutRouteQueryKey} from '../../../page-data/loaders/use-layout-route-data'
import {useHeaderPageDataQueryKey} from '../../../page-data/loaders/use-header-page-data'
import type {HeaderPageData} from '../../../page-data/payloads/header'
import type {PullRequestState} from '../../../types'

/** Invalidate queries tied to the current PR state (open/closed/draft), triggering refetches.
 * We also optionally optimistically update the state in addition to invalidating it.
 */
export function useInvalidatePrState() {
  const queryClient = useQueryClient()
  const overviewQueryKey = useOverviewRouteQueryKey()
  const layoutQueryKey = useLayoutRouteQueryKey()
  const headerQueryKey = useHeaderPageDataQueryKey()

  return async (optimisticState?: PullRequestState) => {
    if (optimisticState) {
      const applyOptimisticState = (old?: HeaderPageData) =>
        old && {...old, pullRequest: {...old.pullRequest, state: optimisticState}}

      queryClient.setQueryData(headerQueryKey, applyOptimisticState)
      if (layoutQueryKey) queryClient.setQueryData<HeaderPageData>(layoutQueryKey, applyOptimisticState)
    }

    // We also need to invalidate the overview route because it owns the 'user' object with allowable state changes,
    // which unfortunately aren't pure permission checks; they depend on the current PR state
    await Promise.all([
      queryClient.invalidateQueries({queryKey: layoutQueryKey}),
      queryClient.invalidateQueries({queryKey: overviewQueryKey}),
      queryClient.invalidateQueries({queryKey: headerQueryKey}),
    ])
  }
}
